/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', (evt) => {
        if (evt.target.classList.contains('is-unavailable')) {
          // 1. Evitar que se pinte: Desmarcamos el radio agotado inmediatamente
          evt.target.checked = false;
          
          // 2. Restaurar la selección anterior (la que tiene stock)
          if (this._lastAvailableOptions && this._lastAvailableOptions.size > 0) {
            this._lastAvailableOptions.forEach((radio) => {
              radio.checked = true;
            });
          }

          // 3. Abrir el popup con la variante agotada que tocaron
          const val = evt.target.value;
          const variant = this.data.variants.find(v => v.options.includes(val));
          if (variant) this.openBisModal(variant.id);
          
          return; // Detenemos la ejecución aquí
        }
        
        // Si tiene stock, ejecutamos el cambio normal
        this.handleVariantChange(evt);
      });
      this.applySearchParams();

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const productUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      if (productUrl) {
        const selectedUrl = new URL(productUrl, window.location.origin);
        if (selectedUrl.pathname !== window.location.pathname) {
          const params = `option_values=${this.getSelectedOptionIds().join(',')}`;
          const separator = productUrl.includes('?') ? '&' : '?';
          window.location = `${productUrl}${separator}${params}`;
          return;
        }
      }

      // If the clicked radio is unavailable, open popup and revert immediately
      if (evt.target && evt.target.classList && evt.target.classList.contains('is-unavailable')) {
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        if (this.variant) {
          this.openBisModal(this.variant.id);
        }
        return;
      }

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // Fallback check
      if (this.variant && !this.variant.available) {
        this.openBisModal(this.variant.id);
        return;
      }

      // Save current available option for future revert
      this.optionSelectors.forEach((selector) => {
        const checked = selector.querySelector('input:checked');
        if (checked && !checked.classList.contains('is-unavailable')) {
          if (!this._lastAvailableOptions) this._lastAvailableOptions = new Map();
          this._lastAvailableOptions.set(selector, checked);
        }
      });

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      this.klaviyoForm = this.klaviyoForm || this.section.querySelector('.klaviyo-form-wrap');

      const variantAvailable = !!(this.variant && this.variant.available);
      const variantInput = this.klaviyoForm.querySelector('input[name="variant_id"]');
      variantInput.value = this.variant.id;

      if (this.addBtn) {
        this.addBtn.classList.toggle('hidden', !variantAvailable);
        this.addBtn.toggleAttribute('disabled', !variantAvailable);
        this.addBtn.setAttribute('type', variantAvailable ? 'submit' : 'button');
      }

      if (this.klaviyoForm) {
        this.klaviyoForm.classList.toggle('hidden', true);
        this.klaviyoForm.setAttribute('aria-hidden', 'true');
      }
    }

    openBisModal(variantId) {
      const modal = this.section.querySelector('[id^="bis-modal-"]') || document.querySelector('[id^="bis-modal-"]');
      if (!modal) return;
      if (modal.parentElement !== document.body) document.body.appendChild(modal);

      const select = modal.querySelector('.bis-modal__select');
      const emailInput = modal.querySelector('.bis-modal__email');
      const submitBtn = modal.querySelector('.bis-modal__submit');
      const successMsg = modal.querySelector('.bis-modal__success');
      const overlay = modal.querySelector('.bis-modal__overlay');
      const closeBtn = modal.querySelector('.bis-modal__close');
      const descEl = modal.querySelector('.bis-modal__desc');
      const productNameEl = modal.querySelector('.bis-modal__product-name');
      const disclaimerEl = modal.querySelector('.bis-modal__disclaimer');

      // Pre-select the variant
      if (select) {
        for (let i = 0; i < select.options.length; i++) {
          if (select.options[i].value == variantId) {
            select.selectedIndex = i;
            break;
          }
        }
      }

      // Reset state
      emailInput.value = '';
      emailInput.style.borderColor = '#ddd';
      submitBtn.style.display = '';
      emailInput.style.display = '';
      select.style.display = '';
      successMsg.style.display = 'none';
      if (descEl) descEl.style.display = '';
      if (productNameEl) productNameEl.style.display = '';
      if (disclaimerEl) disclaimerEl.style.display = '';

      modal.style.display = 'flex';

      const self = this;

      // Deselect unavailable variant and close
      const closeModal = () => {
        modal.style.display = 'none';
        self.deselectUnavailableVariant();
      };

      overlay.onclick = closeModal;
      closeBtn.onclick = closeModal;

      // Submit to Klaviyo
      submitBtn.onclick = () => {
        const email = emailInput.value.trim();
        const selectedVariantId = select.value;
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          emailInput.style.borderColor = 'red';
          return;
        }
        emailInput.style.borderColor = '#ddd';

        // Call Klaviyo via existing component
        const klaviyoFormEl = document.querySelector('.js-back-in-stock');
        if (klaviyoFormEl) {
          const hiddenVariantInput = klaviyoFormEl.querySelector('input[name="variant_id"]');
          if (hiddenVariantInput) hiddenVariantInput.value = selectedVariantId;
        }

        const klaviyoComponent = document.querySelector('klaviyo-form');
        if (klaviyoComponent && klaviyoComponent.callKlaviyo) {
          klaviyoComponent.callKlaviyo(email, selectedVariantId, klaviyoFormEl);
        }

        submitBtn.style.display = 'none';
        emailInput.style.display = 'none';
        select.style.display = 'none';
        if (descEl) descEl.style.display = 'none';
        if (productNameEl) productNameEl.style.display = 'none';
        if (disclaimerEl) disclaimerEl.style.display = 'none';
        successMsg.style.display = '';

        setTimeout(() => {
          modal.style.display = 'none';
          self.deselectUnavailableVariant();
        }, 3000);
      };
    }

    deselectUnavailableVariant() {
      // Desmarcamos cualquier radio que sea "unavailable"
      this.querySelectorAll('.opt-btn:checked').forEach((r) => {
        if (r.classList.contains('is-unavailable')) {
          r.checked = false;
        }
      });

      // Si teníamos una selección previa con stock, la restauramos
      if (this._lastAvailableOptions && this._lastAvailableOptions.size > 0) {
        let lastOption = null;
        this._lastAvailableOptions.forEach((radio) => {
          radio.checked = true;
          lastOption = radio;
        });
        if (lastOption) {
          lastOption.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      // Sin selección previa con stock: actualizamos estado pero NO tocamos el precio
      this.selectedOptions = this.getSelectedOptions();
      this.variant = null;
      this.updateAvailability();

      // Mantenemos el botón visible pero sin acción
      if (this.addBtn) {
        this.addBtn.classList.remove('hidden');
        this.addBtn.removeAttribute('disabled');
        this.addBtn.setAttribute('type', 'submit');
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') return;

      this.optionSelectors.forEach((selector, selectorIndex) => {
        const options = selector.querySelectorAll('.js-option:not([data-value=""])');
        let optionThatExists = false;
        options.forEach((option) => {
          // Skip combined listings
          if (option.dataset.productUrl) return;

          const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
          // any available variants with previous options and this one locked in?
          let variantsExist = false;
          let variantsAvailable = false;
          this.data.variants.forEach((v) => {
            let matches = 0;
            for (let i = 0; i < selectorIndex; i += 1) {
              if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                matches += 1;
              }
            }
            if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
              variantsExist = true;
              if (!optionThatExists) {
                optionThatExists = option;
              }
              if (v.available) {
                variantsAvailable = true;
              }
            }
          });
          VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
        });
        if (this.dataset.availability === 'prune' && optionThatExists && selector.querySelector('.js-option.is-nonexistent:is(:checked, [aria-selected="true"])')) {
          setTimeout(() => {
            if (optionThatExists.closest('custom-select')) {
              optionThatExists.closest('custom-select').selectOption(optionThatExists);
            } else {
              optionThatExists.click();
            }
          }, 0);
        }
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          priceWasEl.querySelector('.js-label').textContent = '';
          priceWasEl.querySelector('.js-value').innerHTML = '';
        }

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
